import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { addHours, format, startOfHour } from 'date-fns';
import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import AttendeePicker, {
  type AttendeeSelection,
} from '../common/AttendeePicker';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  useCreateEvent,
  useDeleteEvent,
  useEventDetail,
  useUpdateEvent,
} from '../../hooks/useCalendarEvents';
import {
  EVENT_COLOR_CLASSES,
  EVENT_COLOR_KEYS,
  type EventColorKey,
  colorForEvent,
} from './eventColors';
import { safeParse } from './calendarDateUtils';
import type {
  CalendarEvent,
  CalendarEventCreate,
  CalendarEventUpdate,
} from '../../types/integrations';

interface EventFormProps {
  mode: 'create' | 'edit';
  event?: CalendarEvent;
  initialDate?: Date;
  onClose: () => void;
}

const eventSchema = z
  .object({
    summary: z
      .string()
      .min(1, 'Title is required')
      .max(255, 'Title must be at most 255 characters'),
    description: z.string().max(8000).optional(),
    location: z.string().max(255).optional(),
    all_day: z.boolean(),
    start: z.string().min(1, 'Start date is required'),
    end: z.string().min(1, 'End date is required'),
    learner_ids: z.array(z.string()),
    learner_group_ids: z.array(z.string()),
    color: z.enum(EVENT_COLOR_KEYS as [EventColorKey, ...EventColorKey[]]),
  })
  .refine(
    (data) => {
      const startMs = new Date(data.start).getTime();
      const endMs = new Date(data.end).getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
      return data.all_day ? endMs >= startMs : endMs > startMs;
    },
    { message: 'End must be after start', path: ['end'] }
  )
  .refine(
    (data) => data.learner_ids.length + data.learner_group_ids.length <= 50,
    { message: 'Maximum 50 attendees combined', path: ['learner_ids'] }
  );

type EventFormValues = z.infer<typeof eventSchema>;

const toLocalInput = (d: Date): string => format(d, "yyyy-MM-dd'T'HH:mm");
const toDateInput = (d: Date): string => format(d, 'yyyy-MM-dd');

const buildDefaults = (
  event: CalendarEvent | undefined,
  initialDate: Date | undefined
): EventFormValues => {
  if (event) {
    return {
      summary: event.summary || '',
      description: event.description || '',
      location: event.location || '',
      all_day: event.all_day,
      start: event.all_day
        ? toDateInput(safeParse(event.start))
        : toLocalInput(safeParse(event.start)),
      end: event.all_day
        ? toDateInput(safeParse(event.end))
        : toLocalInput(safeParse(event.end)),
      learner_ids: [],
      learner_group_ids: [],
      color: colorForEvent(event.id),
    };
  }
  const baseStart = startOfHour(addHours(initialDate ?? new Date(), 1));
  const baseEnd = addHours(baseStart, 1);
  return {
    summary: '',
    description: '',
    location: '',
    all_day: false,
    start: toLocalInput(baseStart),
    end: toLocalInput(baseEnd),
    learner_ids: [],
    learner_group_ids: [],
    color: 'purple',
  };
};

const isoFromInput = (
  raw: string,
  allDay: boolean,
  position: 'start' | 'end'
): string => {
  if (allDay) {
    const datePart = (raw || '').slice(0, 10);
    const [yyyy, mm, dd] = datePart.split('-').map(Number);
    if (!yyyy || !mm || !dd) {
      const fallback = position === 'end' ? addHours(new Date(), 24) : new Date();
      return fallback.toISOString();
    }
    return new Date(Date.UTC(yyyy, mm - 1, dd)).toISOString();
  }
  const value = raw && !raw.includes('T') ? `${raw}T09:00` : raw;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const fallback = position === 'end' ? addHours(new Date(), 1) : new Date();
    return fallback.toISOString();
  }
  return d.toISOString();
};

const EventForm: React.FC<EventFormProps> = ({ mode, event, initialDate, onClose }) => {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [replaceAttendees, setReplaceAttendees] = useState(false);

  // Pull full detail (for current attendee chips) when editing.
  const { data: detail } = useEventDetail(
    isEdit && event ? event.id : null,
    event?.calendar_id
  );
  const currentAttendees = detail?.attendees ?? [];

  const defaults = useMemo(
    () => buildDefaults(event, initialDate),
    [event, initialDate]
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    Object.entries(defaults).forEach(([k, v]) => {
      setValue(k as keyof EventFormValues, v as never, { shouldDirty: false });
    });
  }, [defaults, setValue]);

  const allDay = watch('all_day');
  const selectedColor = watch('color');

  useEffect(() => {
    const start = watch('start');
    const end = watch('end');
    if (allDay) {
      if (start?.includes('T')) {
        setValue('start', start.slice(0, 10), { shouldDirty: true });
      }
      if (end?.includes('T')) {
        setValue('end', end.slice(0, 10), { shouldDirty: true });
      }
    } else {
      if (start && !start.includes('T')) {
        setValue('start', `${start}T09:00`, { shouldDirty: true });
      }
      if (end && !end.includes('T')) {
        setValue('end', `${end}T10:00`, { shouldDirty: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDay]);

  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();
  const deleteMutation = useDeleteEvent();

  const onSubmit = async (values: EventFormValues) => {
    try {
      if (isCreate) {
        const payload: CalendarEventCreate = {
          summary: values.summary,
          description: values.description || undefined,
          location: values.location || null,
          all_day: values.all_day,
          start: isoFromInput(values.start, values.all_day, 'start'),
          end: isoFromInput(values.end, values.all_day, 'end'),
          learner_ids: values.learner_ids,
          learner_group_ids: values.learner_group_ids,
        };
        await createMutation.mutateAsync(payload);
        onClose();
        return;
      }
      if (isEdit && event) {
        // PATCH only dirty fields. start/end ship together per backend rules.
        const patch: CalendarEventUpdate = {};
        if (dirtyFields.summary) patch.summary = values.summary;
        if (dirtyFields.description) patch.description = values.description || undefined;
        if (dirtyFields.location) patch.location = values.location || null;
        if (dirtyFields.all_day) patch.all_day = values.all_day;
        if (dirtyFields.start || dirtyFields.end || dirtyFields.all_day) {
          patch.start = isoFromInput(values.start, values.all_day, 'start');
          patch.end = isoFromInput(values.end, values.all_day, 'end');
        }
        if (replaceAttendees) {
          patch.learner_ids = values.learner_ids;
          patch.learner_group_ids = values.learner_group_ids;
        }
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        await updateMutation.mutateAsync({ eventId: event.id, patch });
        onClose();
      }
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === 'VALIDATION_ERROR' && data?.errors) {
        for (const [field, messages] of Object.entries(data.errors)) {
          const list = Array.isArray(messages) ? messages : [String(messages)];
          setError(field as keyof EventFormValues, {
            message: list[0] ?? 'Invalid value',
          });
        }
      }
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    setConfirmDelete(false);
    try {
      await deleteMutation.mutateAsync({
        eventId: event.id,
        calendarId: event.calendar_id,
      });
      onClose();
    } catch {
      /* hook surfaces toast */
    }
  };

  const canDelete =
    isEdit && event?.is_tereiko && (detail?.tereiko_resource_type === 'ad_hoc');

  const title = isCreate ? 'Add event' : event?.summary || 'Edit event';
  const submitting =
    isSubmitting || createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={title}
        size="2xl"
        footer={
          <div className="flex items-center justify-between gap-3">
            <div>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={submitting || deleteMutation.isPending}
                  className="btn btn-ghost text-error-500 inline-flex items-center gap-2 active:scale-[0.97] transition-transform disabled:opacity-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="btn btn-outline active:scale-[0.97] transition-transform disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="event-form"
                disabled={submitting}
                className="btn btn-primary active:scale-[0.97] transition-transform disabled:opacity-50"
              >
                {submitting
                  ? isCreate ? 'Creating…' : 'Saving…'
                  : isCreate ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        }
      >
        <form id="event-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Title */}
          <div>
            <input
              type="text"
              placeholder="Title"
              autoFocus
              className="w-full px-3 py-3 rounded-2 border border-border-primary/30 bg-surface-primary text-base font-medium text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-primary-500"
              {...register('summary')}
            />
            {errors.summary && (
              <p className="error-text mt-1">{errors.summary.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <textarea
              placeholder="Description"
              rows={3}
              className="w-full px-3 py-2.5 rounded-2 border border-border-primary/30 bg-surface-primary text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-primary-500 resize-none"
              {...register('description')}
            />
            {errors.description && (
              <p className="error-text mt-1">{errors.description.message}</p>
            )}
          </div>

          {/* All day */}
          <Controller
            control={control}
            name="all_day"
            render={({ field }) => (
              <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                <span className="relative inline-block w-9 h-5">
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span className="absolute inset-0 rounded-full bg-border-primary/30 peer-checked:bg-primary-500 transition-colors" />
                  <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white peer-checked:translate-x-4 transition-transform" />
                </span>
                <span className="text-sm font-medium text-text-primary">All day</span>
              </label>
            )}
          />

          {/* Date inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Start date
              </label>
              <Controller
                control={control}
                name="start"
                render={({ field }) => (
                  <input
                    type={allDay ? 'date' : 'datetime-local'}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-2 border border-border-primary/30 bg-surface-primary text-sm text-text-primary focus:outline-none focus:border-primary-500"
                  />
                )}
              />
              {errors.start && (
                <p className="error-text mt-1">{errors.start.message}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                End date
              </label>
              <Controller
                control={control}
                name="end"
                render={({ field }) => (
                  <input
                    type={allDay ? 'date' : 'datetime-local'}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-2 border border-border-primary/30 bg-surface-primary text-sm text-text-primary focus:outline-none focus:border-primary-500"
                  />
                )}
              />
              {errors.end && (
                <p className="error-text mt-1">{errors.end.message}</p>
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <input
              type="text"
              placeholder="Location (optional)"
              className="w-full px-3 py-2.5 rounded-2 border border-border-primary/30 bg-surface-primary text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-primary-500"
              {...register('location')}
            />
            {errors.location && (
              <p className="error-text mt-1">{errors.location.message}</p>
            )}
          </div>

          {/* Attendees */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-text-secondary">
              Invite individuals or Departments
            </label>

            {isEdit && currentAttendees.length > 0 && (
              <div className="rounded-2 border border-border-primary/15 bg-surface-elevated p-3 space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-wider text-text-tertiary">
                  Current attendees ({currentAttendees.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {currentAttendees.slice(0, 5).map((a) => (
                    <span
                      key={a.email}
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-surface-primary text-xs text-text-secondary border border-border-primary/15"
                    >
                      {a.display_name || a.email}
                    </span>
                  ))}
                  {currentAttendees.length > 5 && (
                    <span className="text-xs text-text-tertiary">
                      +{currentAttendees.length - 5} more
                    </span>
                  )}
                </div>
                <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={replaceAttendees}
                    onChange={(e) => setReplaceAttendees(e.target.checked)}
                    className="w-3.5 h-3.5 accent-primary-500"
                  />
                  Replace attendee list
                </label>
              </div>
            )}

            {(isCreate || replaceAttendees) && (
              <Controller
                control={control}
                name="learner_ids"
                render={() => (
                  <Controller
                    control={control}
                    name="learner_group_ids"
                    render={() => {
                      const value: AttendeeSelection = {
                        learner_ids: watch('learner_ids'),
                        learner_group_ids: watch('learner_group_ids'),
                      };
                      return (
                        <AttendeePicker
                          value={value}
                          onChange={(next) => {
                            setValue('learner_ids', next.learner_ids, {
                              shouldDirty: true,
                            });
                            setValue('learner_group_ids', next.learner_group_ids, {
                              shouldDirty: true,
                            });
                          }}
                        />
                      );
                    }}
                  />
                )}
              />
            )}
            {errors.learner_ids && (
              <p className="error-text mt-1">{errors.learner_ids.message}</p>
            )}
          </div>

          {/* Color swatches */}
          <div>
            <div className="flex items-center gap-2">
              {EVENT_COLOR_KEYS.map((key) => {
                const c = EVENT_COLOR_CLASSES[key];
                const active = selectedColor === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setValue('color', key, { shouldDirty: true })}
                    aria-label={key}
                    aria-pressed={active}
                    className={[
                      'w-7 h-7 rounded-full flex items-center justify-center',
                      c.swatch,
                      'active:scale-[0.93] transition-transform',
                      active ? 'ring-2 ring-offset-2 dark:ring-offset-neutral-900 ring-primary-500' : 'opacity-90 hover:opacity-100',
                    ].join(' ')}
                  >
                    {active && <CheckIcon className="w-4 h-4 text-white" />}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-text-tertiary mt-1">
              Color is a visual hint and is not yet persisted.
            </p>
          </div>
        </form>
      </Modal>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete This Event?"
          message="This will remove the event from your Tereiko calendar and notify attendees."
          confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          confirmVariant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
};

export default EventForm;
